import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon, Sparkles, BookOpen, ScrollText, Fingerprint, ArrowRight, Clock, ChevronRight, Users, Crown, Star, Orbit, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fetchCurrentMoon, fetchLunarClimate, fetchNakshatra } from "@/lib/api";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/AuthContext";
import NavBar from "@/components/NavBar";
import UpcomingEvents from "@/components/UpcomingEvents";
import EclipseCountdownWidget from "@/components/EclipseCountdownWidget";
import WeeklyDigestSubscribe from "@/components/WeeklyDigestSubscribe";
import DailyHoroscopeWidget from "@/components/DailyHoroscopeWidget";
import { MoonPlacement } from "@/components/MoonPlacement";
import { ForecastListen } from "@/components/ForecastListen";
import QuickCompatibility from "@/components/QuickCompatibility";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

function formatHours(h) {
  if (!h || h <= 0) return "—";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
}

export default function Landing() {
  const [moonData, setMoonData] = useState(null);
  const [climate, setClimate] = useState(null);
  const [nakshatra, setNakshatra] = useState(null);
  const [loading, setLoading] = useState(true);

  // Plain-text narration for the "Listen to the forecast" feature (matches the displayed text)
  const narrationText = useMemo(() => {
    if (!climate) return "";
    const parts = [];
    if (climate.moon_placement) parts.push(climate.moon_placement);
    const body = (climate.climate_text || "")
      .replace(climate.moon_placement || "", "")
      .replace(/[#*_`>]/g, "")
      .replace(/^\s*[-•]\s*/gm, "")
      .split(/\n+/)
      .map((s) => s.trim())
      .filter(Boolean);
    parts.push(...body);
    return parts.join(". \n");
  }, [climate]);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    async function load() {
      try {
        const moon = await fetchCurrentMoon();
        setMoonData(moon);
        // Pass premium flag based on user status
        const isPremium = user?.is_premium || false;
        const [climateData, nakData] = await Promise.all([
          fetchLunarClimate(isPremium).catch(() => null),
          fetchNakshatra(moon.nakshatra_slug).catch(() => null),
        ]);
        setClimate(climateData);
        setNakshatra(nakData);
      } catch (e) {
        console.error("Failed to load moon data:", e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]" data-testid="loading-spinner">
        <div className="flex flex-col items-center gap-4">
          <img src="/logo.png" alt="Daily Nakshatra" className="h-12 w-auto logo-pulse" />
          <p className="text-white/40 text-sm tracking-widest uppercase font-[Manrope]">Loading lunar data...</p>
        </div>
      </div>
    );
  }

  const imageUrl = nakshatra?.featured_image_url
    ? (nakshatra.featured_image_url.startsWith("http") ? nakshatra.featured_image_url : nakshatra.featured_image_url)
    : "https://images.pexels.com/photos/9665214/pexels-photo-9665214.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

  const deepDiveButtons = [
    { label: "Birth Traits", icon: Fingerprint, section: "birth-traits" },
    { label: "Mythology", icon: ScrollText, section: "mythology" },
    { label: "Symbolism", icon: Sparkles, section: "symbolism" },
    { label: "Padas", icon: BookOpen, section: "padas" },
  ];

  return (
    <div className="min-h-screen bg-[#050505]">
      <NavBar />

      <main className="pt-14">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-8" data-testid="hero-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Nakshatra Index */}
            <Link to="/nakshatras" className="inline-block group" data-testid="nakshatra-index-link">
              <p className="text-white/30 text-xs tracking-[0.3em] uppercase font-[Manrope] mb-3 group-hover:text-white/50 transition-colors duration-200 cursor-pointer">
                Nakshatra {moonData?.nakshatra_index} of 27
              </p>
            </Link>

            {/* Nakshatra Name */}
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl font-light text-white/95 mb-3"
              data-testid="current-nakshatra-name"
            >
              {moonData?.nakshatra_name}
            </h1>

            {/* Pada + Moon Position - now showing both tropical sign and sidereal position */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
              <Link
                to={`/nakshatras/${moonData?.nakshatra_slug}?section=padas`}
                className="text-white/70 text-base hover:text-white transition-colors duration-200 border-b border-white/10 hover:border-white/40 pb-0.5"
                data-testid="current-pada-link"
              >
                Pada {moonData?.pada}
              </Link>
              <span className="text-white/30">|</span>
              <span className="text-white/50 text-sm" data-testid="moon-tropical-position">
                Moon in {moonData?.tropical_sign} ({moonData?.tropical_degree_in_sign}°)
              </span>
            </div>

            {/* Calculation System Label */}
            <div className="flex items-center gap-2 mb-8">
              <span className="text-white/25 text-[10px] tracking-[0.15em] uppercase font-[Manrope] bg-white/5 px-2 py-1 rounded" data-testid="calculation-system-label">
                {moonData?.ayanamsa_name || "True Lahiri"}
              </span>
            </div>

            {/* Time Remaining */}
            <div className="flex flex-wrap gap-4 mb-10">
              {moonData?.hours_to_next_pada && (
                <div className="flex items-center gap-2 text-white/40 text-sm" data-testid="time-to-next-pada">
                  <Clock className="w-3.5 h-3.5" />
                  <span>Next pada in {formatHours(moonData.hours_to_next_pada)}</span>
                </div>
              )}
              {moonData?.hours_to_next_nakshatra && (
                <div className="flex items-center gap-2 text-white/40 text-sm" data-testid="time-to-next-nakshatra">
                  <ChevronRight className="w-3.5 h-3.5" />
                  <span>{moonData.next_nakshatra_name} in {formatHours(moonData.hours_to_next_nakshatra)}</span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Featured Image */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <Link to={`/nakshatras/${moonData?.nakshatra_slug}`} data-testid="featured-image-link">
              <div className="relative w-full max-w-xl aspect-[4/5] overflow-hidden rounded-lg group cursor-pointer">
                <img
                  src={imageUrl}
                  alt={moonData?.nakshatra_name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  data-testid="featured-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <p className="text-white/90 text-lg font-[Cormorant_Garamond]">
                    Tap to explore
                  </p>
                </div>
              </div>
            </Link>
          </motion.div>
        </section>

        {/* Lunar Climate Report */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="lunar-climate-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <h2 className="text-3xl sm:text-4xl font-light text-white/90">Lunar Climate</h2>
              {climate && narrationText && <ForecastListen text={narrationText} />}
            </div>

            {climate ? (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Climate Text - Enhanced Markdown Rendering */}
                <div className="lg:col-span-2 rounded-lg p-6 sm:p-8" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="climate-text">
                  <MoonPlacement
                    sign={climate.moon_tropical_sign || climate.moon_sign}
                    nakshatra={climate.nakshatra_name}
                    nakshatraSlug={climate.nakshatra_slug}
                    pada={climate.pada}
                    className="mb-6 pb-6 border-b border-white/10"
                  />
                  <div 
                    className="lunar-climate-content"
                    dangerouslySetInnerHTML={{
                      __html: (climate.climate_text || "")
                        // Remove the baked placement sentence (shown prominently above instead)
                        .replace(climate.moon_placement || "", "")
                        // Convert H3 subheaders (###) before H2
                        .replace(/^### (.*?)$/gm, '<h4 class="text-white/70 text-xs font-semibold tracking-[0.15em] uppercase mt-5 mb-2">$1</h4>')
                        // Convert markdown headers
                        .replace(/^## (.*?)$/gm, '<h3 class="text-white/90 text-lg font-medium mt-6 mb-3 first:mt-0">$1</h3>')
                        // Convert bold text
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white/90">$1</strong>')
                        // Convert bullet points - handle both • and - style
                        .replace(/^[•\-]\s*(.*?)$/gm, '<li class="text-white/70 text-sm leading-relaxed pl-4 relative before:content-[\'\'] before:absolute before:left-0 before:top-2 before:w-1.5 before:h-1.5 before:rounded-full before:bg-amber-500/40 mb-2">$1</li>')
                        // Wrap consecutive list items in ul
                        .replace(/(<li.*?<\/li>\n?)+/g, '<ul class="space-y-1 my-4">$&</ul>')
                        // Convert paragraphs (lines that aren't headers or list items)
                        .replace(/^(?!<[hul])(.*?)$/gm, (match) => {
                          if (match.trim() && !match.startsWith('<')) {
                            return `<p class="text-white/60 text-sm leading-relaxed mb-3">${match}</p>`;
                          }
                          return match;
                        })
                        // Clean up empty paragraphs
                        .replace(/<p[^>]*>\s*<\/p>/g, '')
                        // Add line breaks for remaining newlines
                        .replace(/\n\n/g, '')
                        .replace(/\n/g, '')
                    }}
                  />
                </div>

                {/* Aspects Sidebar */}
                <div className="rounded-lg p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }} data-testid="aspects-sidebar">
                  {/* Moon Position Summary */}
                  <div className="mb-6 pb-4 border-b border-white/5">
                    <h3 className="text-sm tracking-[0.2em] uppercase text-white/40 mb-3 font-[Manrope]">Current Position</h3>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                        <Moon className="w-5 h-5 text-amber-400/70" />
                      </div>
                      <div>
                        <p className="text-white/80 text-sm font-medium">{climate.nakshatra_name}</p>
                        <p className="text-white/40 text-xs">Pada {climate.pada} · {climate.moon_degree?.toFixed(1)}° {climate.moon_sign}</p>
                      </div>
                    </div>
                  </div>

                  <h3 className="text-sm tracking-[0.2em] uppercase text-white/40 mb-4 font-[Manrope]">Active Aspects</h3>
                  {climate.aspects && climate.aspects.length > 0 ? (
                    <div className="space-y-3">
                      {climate.aspects.map((asp, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                          <div>
                            <p className="text-white/80 text-sm capitalize">{asp.type}</p>
                            <p className="text-white/40 text-xs">{asp.planet}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white/60 text-sm">{asp.orb}°</p>
                            <p className="text-xs" style={{ color: asp.applying ? "#10B981" : "#52525B" }}>
                              {asp.applying ? "applying" : "separating"}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-white/30 text-sm">No major aspects active — today's energy is self-contained.</p>
                  )}

                  {/* Quick Guidance from Pointers */}
                  {climate.pointers && climate.pointers.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-white/5">
                      <h3 className="text-sm tracking-[0.2em] uppercase text-white/40 mb-3 font-[Manrope]">Quick Guidance</h3>
                      <ul className="space-y-2">
                        {climate.pointers.slice(0, 3).map((p, i) => (
                          <li key={i} className="text-white/50 text-xs pl-3 relative before:content-[''] before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-white/20">
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-lg p-8 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                <p className="text-white/30 text-sm">Generating lunar climate report...</p>
              </div>
            )}
          </motion.div>
        </section>

        {/* Eclipse Countdown Widget - shows upcoming eclipses within range */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="eclipse-countdown-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
          >
            <EclipseCountdownWidget maxDays={180} />
          </motion.div>
        </section>

        {/* Upcoming Celestial Events */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="upcoming-events-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <UpcomingEvents showTitle={true} initialLimit={6} maxDays={90} />
          </motion.div>
        </section>

        {/* Weekly Digest Subscription */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="weekly-digest-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <WeeklyDigestSubscribe variant="default" />
          </motion.div>
        </section>

        {/* Daily Horoscope & Quick Compatibility */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="widgets-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Daily Horoscope */}
              <DailyHoroscopeWidget />
              
              {/* Quick Compatibility */}
              <QuickCompatibility />
            </div>
          </motion.div>
        </section>

        {/* Deep Dive Buttons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="deep-dive-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h2 className="text-3xl sm:text-4xl font-light text-white/90 mb-6">Deep Dive</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {deepDiveButtons.map((btn) => (
                <Link
                  key={btn.section}
                  to={`/nakshatras/${moonData?.nakshatra_slug}?section=${btn.section}`}
                  data-testid={`deep-dive-${btn.section}`}
                >
                  <div className="rounded-lg p-5 flex flex-col items-start gap-3 transition-colors duration-200 hover:bg-white/[0.06] cursor-pointer" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                    <btn.icon className="w-5 h-5 text-white/50" />
                    <span className="text-white/80 text-sm font-medium">{btn.label}</span>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Premium Features */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="premium-features-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.45 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Crown className="w-5 h-5 text-amber-400/80" />
              <h2 className="text-3xl sm:text-4xl font-light text-white/90">Premium Reports</h2>
            </div>
            <div className="flex items-center gap-2 mb-6">
              <Star className="w-4 h-4 text-emerald-400" />
              <p className="text-emerald-400 text-sm">Premium Lunar Climate is now free for everyone!</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Dasha Forecast */}
              <Link to="/dasha" data-testid="premium-dasha-link">
                <div className="rounded-lg p-6 transition-all duration-200 hover:bg-white/[0.06] cursor-pointer group" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-amber-400/80" />
                    </div>
                    <div>
                      <p className="text-white/90 font-medium">Dasha Forecast</p>
                      <p className="text-white/30 text-xs">$1.99 • <span className="text-amber-400/70">Included in Premium</span></p>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Vimshottari Mahadasha & Antardasha — your current planetary period and what it activates.
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-white/40 text-xs group-hover:text-white/60 transition-colors">
                    <span>Get your forecast</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>

              {/* Compatibility */}
              <Link to="/compatibility" data-testid="premium-compatibility-link">
                <div className="rounded-lg p-6 transition-all duration-200 hover:bg-white/[0.06] cursor-pointer group" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-pink-500/10 flex items-center justify-center">
                      <Users className="w-5 h-5 text-pink-400/80" />
                    </div>
                    <div>
                      <p className="text-white/90 font-medium">Compatibility</p>
                      <p className="text-white/30 text-xs">$3.99 • <span className="text-amber-400/70">Included in Premium</span></p>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Nakshatra synastry — explore the emotional resonance between two lunar placements.
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-white/40 text-xs group-hover:text-white/60 transition-colors">
                    <span>Check compatibility</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>

              {/* Karmic Timeline */}
              <Link to="/karmic" data-testid="premium-karmic-link">
                <div className="rounded-lg p-6 transition-all duration-200 hover:bg-white/[0.06] cursor-pointer group" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-violet-500/10 flex items-center justify-center">
                      <Orbit className="w-5 h-5 text-violet-300/80" />
                    </div>
                    <div>
                      <p className="text-white/90 font-medium">Karmic Timeline</p>
                      <p className="text-white/30 text-xs">$1.99 • <span className="text-amber-400/70">4 free/mo in Premium</span></p>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">
                    Understand your current chapter—and what's unfolding next. A personalized, rolling 18-month overview of your developmental themes using tropical Vedic astrology.
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-white/40 text-xs group-hover:text-white/60 transition-colors">
                    <span>Explore your axis</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>

              {/* Premium Subscription */}
              <Link to="/pricing" data-testid="premium-subscription-link">
                <div className="rounded-lg p-6 transition-all duration-200 hover:bg-white/[0.06] cursor-pointer group" style={{ background: "linear-gradient(135deg, rgba(251,191,36,0.05) 0%, rgba(255,255,255,0.03) 100%)", border: "1px solid rgba(251,191,36,0.15)" }}>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <Crown className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white/90 font-medium">Premium</p>
                      <p className="text-amber-400/80 text-xs">$4.29/mo • 4 reports included</p>
                    </div>
                  </div>
                  <p className="text-white/50 text-sm leading-relaxed">
                    4 reports/month included, additional at $1.00 each. Natal, Compatibility, Dasha & more.
                  </p>
                  <div className="flex items-center gap-1 mt-4 text-amber-400/60 text-xs group-hover:text-amber-400/80 transition-colors">
                    <span>View plans</span>
                    <ArrowRight className="w-3 h-3" />
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>
        </section>

        {/* Natal Calculator CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-20" data-testid="calculator-cta-section">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="rounded-lg p-8 sm:p-12 text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <h2 className="text-3xl sm:text-4xl font-light text-white/90 mb-3">Find Your Natal Nakshatra</h2>
              <p className="text-white/40 text-sm mb-6 max-w-md mx-auto">
                Enter your birth details to discover your Moon's Nakshatra and Pada placement.
              </p>
              <Button
                className="bg-white text-black hover:bg-white/90 rounded-full px-8 py-3 text-sm tracking-wider"
                onClick={() => navigate("/calculator")}
                data-testid="calculator-cta-btn"
              >
                Calculate Now
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        </section>
      </main>

      <footer className="border-t border-white/5 mt-12 py-6">
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[11px] tracking-wider font-[Manrope]">
          <button
            onClick={() => navigate("/privacy")}
            className="text-white/30 hover:text-white/60 transition-colors"
            data-testid="footer-privacy-btn"
          >
            Privacy Policy
          </button>
          <button
            onClick={() => navigate("/delete-account")}
            className="text-white/30 hover:text-white/60 transition-colors"
            data-testid="footer-delete-account-btn"
          >
            Delete Account
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="text-white/15 hover:text-white/40 transition-colors"
            data-testid="footer-admin-btn"
          >
            Admin
          </button>
        </div>
        <p className="text-center text-white/15 text-[11px] mt-3 font-[Manrope]">© {new Date().getFullYear()} Daily Nakshatra</p>
      </footer>
    </div>
  );
}
